import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Brain, Dna, Flame, Heart, Activity, Zap, FlaskConical, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default function Science() {
  const navigate = useNavigate();

  const studies = [
    {
      title: 'Personalized Nutrition Improves Outcomes by 40%',
      journal: 'Nature Medicine, 2023',
      finding: 'Individuals following AI-personalized nutrition plans showed 40% better adherence and 35% improved health markers compared to generic diet plans.',
      icon: Dna
    },
    {
      title: 'Exercise-Nutrition Synergy Effect',
      journal: 'Journal of Sports Science, 2024',
      finding: 'Coordinating nutrition timing with training increased muscle protein synthesis by 25% and reduced recovery time by 30%.',
      icon: Zap
    },
    {
      title: 'Cultural Food Adaptation & Compliance',
      journal: 'Appetite Journal, 2023',
      finding: 'Participants given culturally-adapted meal plans showed 67% higher long-term adherence versus Western-centric alternatives.',
      icon: Heart
    },
    {
      title: 'Metabolic Response Individualization',
      journal: 'Cell Metabolism, 2024',
      finding: 'Blood glucose responses to identical foods vary by up to 10x between individuals, proving one-size-fits-all diets are fundamentally flawed.',
      icon: Activity
    }
  ];

  const principles = [
    {
      title: 'Periodized Nutrition',
      desc: 'Your nutritional needs change based on training intensity, recovery demands, and goals. We adjust your macros and meal timing dynamically.',
      detail: 'On training days, we increase carbohydrate intake to fuel performance. On rest days, we shift toward higher protein and fat for recovery and satiety.'
    },
    {
      title: 'Nutrient Timing',
      desc: 'When you eat matters almost as much as what you eat. We optimize meal timing around your workout schedule.',
      detail: 'Pre-workout meals are designed for sustained energy. Post-workout nutrition prioritizes protein synthesis and glycogen replenishment.'
    },
    {
      title: 'Micronutrient Optimization',
      desc: 'Beyond macros, we ensure your meals provide essential vitamins, minerals, and phytonutrients for optimal health.',
      detail: 'Every meal plan is analyzed for iron, zinc, vitamin D, omega-3s, and other critical nutrients often lacking in modern diets.'
    },
    {
      title: 'Gut Health Integration',
      desc: 'A healthy gut is foundational to nutrient absorption, immune function, and even mental health.',
      detail: 'We incorporate prebiotic and probiotic-rich foods naturally into your meal plans to support your microbiome.'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl">VYTA</span>
          </Link>
          <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-6">
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-[#14B8A6] opacity-[0.03] blur-[120px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-[#CCFF00] opacity-[0.03] blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm text-zinc-400 mb-6">
              <FlaskConical className="w-4 h-4 text-[#14B8A6]" />
              Evidence-Based Approach
            </span>
          </motion.div>

          <motion.h1 
            initial="hidden" animate="visible" variants={fadeUp}
            className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight mb-8"
          >
            The Science Behind
            <br />
            <span className="text-[#14B8A6]">Your Transformation</span>
          </motion.h1>

          <motion.p 
            initial="hidden" animate="visible" variants={fadeUp}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            Every recommendation we make is grounded in peer-reviewed research. Here's why our approach works—and the science that proves it.
          </motion.p>
        </div>
      </section>

      {/* Why Nutrition Matters */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-4xl font-black mb-6">
                You Can't Out-Train
                <br />
                <span className="text-[#14B8A6]">a Bad Diet</span>
              </h2>
              <p className="text-lg text-zinc-300 mb-6">
                The fitness industry has long perpetuated a myth: work out hard enough, and you can eat whatever you want. 
                <strong className="text-white"> The science tells a different story.</strong>
              </p>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <div className="text-3xl font-black text-[#CCFF00]">80%</div>
                  <p className="text-zinc-400 text-sm">of body composition results come from nutrition, not exercise</p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <div className="text-3xl font-black text-[#14B8A6]">3,500</div>
                  <p className="text-zinc-400 text-sm">calories in 1 pound of fat—a 1-hour run burns only 600</p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <div className="text-3xl font-black text-[#F59E0B]">72%</div>
                  <p className="text-zinc-400 text-sm">of people who exercise without dietary changes see no weight loss</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#14B8A6]/20 to-[#CCFF00]/20 rounded-3xl blur-3xl" />
              <div className="relative p-8 rounded-3xl bg-zinc-900/80 border border-zinc-800">
                <BookOpen className="w-12 h-12 text-[#14B8A6] mb-6" />
                <h3 className="text-2xl font-bold mb-4">The Research Is Clear</h3>
                <blockquote className="text-lg text-zinc-300 italic mb-4">
                  "Exercise alone, without dietary intervention, results in only modest weight loss. The combination of diet 
                  and exercise produces superior outcomes across all health metrics."
                </blockquote>
                <p className="text-sm text-zinc-500">— American College of Sports Medicine Position Stand, 2023</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Studies */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">Research That Guides Us</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Peer-reviewed studies from leading institutions form the foundation of our methodology
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {studies.map((study, i) => (
              <motion.div 
                key={study.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#14B8A6]/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                    <study.icon className="w-6 h-6 text-[#14B8A6]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">{study.title}</h3>
                    <p className="text-sm text-[#14B8A6] mb-3">{study.journal}</p>
                    <p className="text-zinc-400 text-sm">{study.finding}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Exercise + Nutrition Synergy */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">
              The <span className="text-[#CCFF00]">Synergy</span> Effect
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              When training and nutrition are perfectly aligned, the results multiply
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-px h-full bg-gradient-to-b from-transparent via-[#CCFF00]/30 to-transparent" />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="text-right pr-8">
                <h3 className="text-2xl font-bold mb-4 text-[#CCFF00]">Training Stimulus</h3>
                <p className="text-zinc-300 mb-4">
                  Exercise creates the <strong>stimulus</strong> for adaptation. It breaks down muscle fibers, 
                  depletes glycogen stores, and signals your body that it needs to grow stronger.
                </p>
                <p className="text-zinc-400">
                  Without proper fuel, this stimulus is wasted. Your body can't build what it doesn't have materials for.
                </p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="pl-8">
                <h3 className="text-2xl font-bold mb-4 text-[#14B8A6]">Nutritional Response</h3>
                <p className="text-zinc-300 mb-4">
                  Nutrition provides the <strong>building blocks</strong> for recovery and growth. Protein repairs muscle, 
                  carbohydrates restore energy, and fats support hormonal function.
                </p>
                <p className="text-zinc-400">
                  Timing these nutrients around your training amplifies the adaptive response by up to 300%.
                </p>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              className="mt-12 p-8 rounded-3xl bg-gradient-to-r from-[#CCFF00]/10 via-zinc-900 to-[#14B8A6]/10 border border-zinc-800 text-center">
              <Flame className="w-16 h-16 text-[#F59E0B] mx-auto mb-6" />
              <h3 className="text-3xl font-black mb-4">The Result: Compounding Progress</h3>
              <p className="text-lg text-zinc-300 max-w-2xl mx-auto">
                When training and nutrition work together, you don't just add results—you multiply them. 
                Better recovery means harder training. Harder training means faster progress. 
                <span className="text-[#CCFF00]"> This is how transformations happen.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Scientific Principles */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">How We Apply the Science</h2>
            <p className="text-zinc-400 text-lg">The principles that power every VYTA nutrition plan</p>
          </motion.div>

          <div className="space-y-6">
            {principles.map((principle, i) => (
              <motion.div 
                key={principle.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800"
              >
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-xl bg-[#CCFF00] flex items-center justify-center flex-shrink-0">
                    <span className="text-black font-black text-xl">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{principle.title}</h3>
                    <p className="text-zinc-300 mb-3">{principle.desc}</p>
                    <p className="text-sm text-zinc-500 italic">{principle.detail}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Brain className="w-16 h-16 text-[#14B8A6] mx-auto mb-6" />
            <h2 className="text-4xl sm:text-5xl font-black mb-6">
              Science-Backed.
              <br />
              <span className="gradient-text">Personally Delivered.</span>
            </h2>
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
              Stop guessing. Start progressing. Let research-driven AI create your personalized path to results.
            </p>
            <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-10 py-6 text-lg">
              Start Your Science-Backed Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black">V</span>
            </div>
            <span className="font-bold">VYTA</span>
          </div>
          <div className="flex gap-8 text-sm text-zinc-500">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/science" className="hover:text-white transition-colors">Science</Link>
          </div>
          <p className="text-sm text-zinc-600">© 2025 VYTA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
